// Geo master client — country / state / city from the backend DB master
// (GET /api/geo/*). Replaces the old static COUNTRY_NAMES list and the
// countriesnow.space calls. Exposes a NAME-based interface (forms still store
// names) and caches results so cascading dropdowns don't refetch.
import api from './api';

export interface GeoItem { id: string; name: string; [k: string]: any }

let _countries: GeoItem[] | null = null;
let _countriesPromise: Promise<GeoItem[]> | null = null;
const _states = new Map<string, GeoItem[]>(); // key: countryName
const _cities = new Map<string, GeoItem[]>();  // key: `${countryName}|${stateName}`

async function loadCountries(): Promise<GeoItem[]> {
  if (_countries) return _countries;
  if (!_countriesPromise) {
    _countriesPromise = api.get('/geo/countries')
      .then(r => { _countries = Array.isArray(r.data) ? r.data : []; return _countries; })
      .catch(() => { _countriesPromise = null; return []; });
  }
  return _countriesPromise;
}

/** All country objects ({ id, name, isoCode, dialCode, flagEmoji }). */
export async function getCountries(): Promise<GeoItem[]> {
  return loadCountries();
}

/** Country names only — drop-in for the old COUNTRY_NAMES list. */
export async function getCountryNames(): Promise<string[]> {
  return (await loadCountries()).map(c => c.name);
}

async function ensureCountryId(name: string): Promise<string | null> {
  const list = await loadCountries();
  return list.find(c => c.name === name)?.id ?? null;
}

/** State names for a country (by country NAME). */
export async function getStateNames(countryName: string): Promise<string[]> {
  if (!countryName) return [];
  if (!_states.has(countryName)) {
    const cid = await ensureCountryId(countryName);
    if (!cid) return [];
    try {
      const r = await api.get('/geo/states', { params: { countryId: cid } });
      _states.set(countryName, Array.isArray(r.data) ? r.data : []);
    } catch { return []; }
  }
  return (_states.get(countryName) ?? []).map(s => s.name);
}

/** City names for a country+state (by NAMEs). */
export async function getCityNames(countryName: string, stateName: string): Promise<string[]> {
  if (!countryName || !stateName) return [];
  const key = `${countryName}|${stateName}`;
  if (!_cities.has(key)) {
    await getStateNames(countryName); // ensure states cached so we can resolve the id
    const sid = (_states.get(countryName) ?? []).find(s => s.name === stateName)?.id ?? null;
    if (!sid) return [];
    try {
      const r = await api.get('/geo/cities', { params: { stateId: sid } });
      _cities.set(key, Array.isArray(r.data) ? r.data : []);
    } catch { return []; }
  }
  return (_cities.get(key) ?? []).map(c => c.name);
}

// ── Sync name → id lookups (from the cache the dropdowns already populated) ──
// Used on form submit / filter apply to send master ids in the request. Return
// null on a cache miss; callers send the name too, which the backend resolves.

export function countryIdByName(name?: string | null): string | null {
  if (!name) return null;
  return (_countries ?? []).find(c => c.name === name)?.id ?? null;
}

export function stateIdByName(countryName?: string | null, stateName?: string | null): string | null {
  if (!countryName || !stateName) return null;
  return (_states.get(countryName) ?? []).find(s => s.name === stateName)?.id ?? null;
}

export function cityIdByName(countryName?: string | null, stateName?: string | null, cityName?: string | null): string | null {
  if (!countryName || !stateName || !cityName) return null;
  return (_cities.get(`${countryName}|${stateName}`) ?? []).find(c => c.name === cityName)?.id ?? null;
}
